import { useState } from 'react';
import { Send, CheckCircle, AlertCircle, TrendingUp, Shield, Eye, Info } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import PageLayout from '@/components/PageLayout';
import { supabase } from '@/integrations/supabase/client';
import { useAvailableStocks } from '@/hooks/useAvailableStocks';
import { sendEmailNotification } from '@/utils/emailUtils';
const Contact = () => {
  const {
    toast
  } = useToast();
  const {
    data: availableStocks,
    isLoading: stocksLoading
  } = useAvailableStocks();
  const [formData, setFormData] = useState({
    email: '',
    startingAmount: '',
    accreditedConfirmed: false,
    selectedStocks: [] as string[]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const startingAmountOptions = [{
    value: '50000',
    label: '$50,000'
  }, {
    value: '100000',
    label: '$100,000'
  }, {
    value: '1000000',
    label: '$1,000,000'
  }];
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      email: e.target.value
    }));
  };
  const handleStartingAmountChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      startingAmount: value
    }));
  };
  const handleAccreditedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      accreditedConfirmed: e.target.checked
    }));
  };
  const handleStockSelection = (stockSymbol: string, stockName: string) => {
    setFormData(prev => {
      const isSelected = prev.selectedStocks.some(stock => stock.split('|')[0] === stockSymbol);
      if (isSelected) {
        // Remove stock
        return {
          ...prev,
          selectedStocks: prev.selectedStocks.filter(stock => stock.split('|')[0] !== stockSymbol)
        };
      } else if (prev.selectedStocks.length < 2) {
        // Add stock (max 2)
        return {
          ...prev,
          selectedStocks: [...prev.selectedStocks, `${stockSymbol}|${stockName}`]
        };
      }
      return prev;
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.startingAmount || !formData.accreditedConfirmed || formData.selectedStocks.length === 0) {
      toast({
        title: "Please Complete All Fields",
        description: "Make sure to fill out all required fields and select at least one stock.",
        variant: "destructive"
      });
      return;
    }
    setIsSubmitting(true);
    try {
      console.log('Submitting signup with data:', formData);

      // Prepare stock selections as JSON array
      const stockSelections = formData.selectedStocks.map(stock => {
        const [symbol, name] = stock.split('|');
        return {
          symbol,
          name
        };
      });

      // Insert consolidated signup record
      const {
        error: signupError
      } = await supabase.from('sample_trade_signups').insert([{
        email: formData.email,
        starting_amount: parseInt(formData.startingAmount),
        accredited_confirmed: formData.accreditedConfirmed,
        status: 'active',
        selected_stocks: stockSelections
      }]);
      if (signupError) {
        console.error('Signup error:', signupError);
        throw signupError;
      }
      console.log('Signup successful for:', formData.email);

      // Send email notification to administrator
      const selectedAmountLabel = startingAmountOptions.find(opt => opt.value === formData.startingAmount)?.label;
      const selectedStocksList = formData.selectedStocks.map(stock => {
        const [symbol, name] = stock.split('|');
        return `${symbol} (${name})`;
      }).join(', ');
      const additionalInfo = {
        email: formData.email,
        startingAmount: selectedAmountLabel,
        selectedStocks: selectedStocksList,
        accreditedConfirmed: formData.accreditedConfirmed ? 'Yes' : 'No'
      };
      await sendEmailNotification('Transparency Experience Signup', additionalInfo, formData.email);
      toast({
        title: "Welcome to Real-Time Trading Transparency!",
        description: `You'll receive live trade notifications for your selected stocks with a starting amount of ${selectedAmountLabel}. Experience our trades as they happen - no hindsight bias, no cherry-picked results.`
      });

      // Reset form
      setFormData({
        email: '',
        startingAmount: '',
        accreditedConfirmed: false,
        selectedStocks: []
      });
    } catch (error: any) {
      console.error('Failed to submit signup:', error);
      if (error.code === '23505') {
        toast({
          title: "Already Registered",
          description: "This email is already registered for our sample trade program.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Registration Failed",
          description: "There was an issue processing your registration. Please try again.",
          variant: "destructive"
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const domesticStocks = availableStocks || [];
  return <PageLayout>
      <main className="pt-16 sm:pt-20">
        {/* Hero Section */}
        <section className="py-12 sm:py-16 md:py-20 relative overflow-hidden grid-bg">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-3xl xs:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">See for Yourself.</h1>
              <h2 className="text-xl xs:text-2xl md:text-3xl text-delphi-red mb-6 sm:mb-8 font-semibold">Verify Our Trades As They Happen</h2>
              <p className="text-lg xs:text-xl text-delphi-light/70 mb-4 sm:mb-6 max-w-3xl mx-auto">
                Receive our actual trade decisions as we make them - not after the fact. 
                Validate our process in real-time to eliminate any possibility of hindsight bias or data manipulation.
              </p>
              <div className="flex items-center justify-center gap-6 text-sm sm:text-base text-delphi-light/60">
                <div className="flex items-center gap-2">
                  <Eye size={16} />
                  <span>Live Decisions</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield size={16} />
                  <span>No Cherry-Picking</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp size={16} />
                  <span>Real-Time Validation</span>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Main Content Section */}
        <section className="py-10 sm:py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
                {/* Left Sidebar - Transparency Benefits */}
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">Complete Transparency</h2>
                  
                  <div className="space-y-8 sm:space-y-10">
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-8 bg-delphi-red/20 rounded-full flex items-center justify-center">
                          <Eye size={16} className="text-delphi-red" />
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold">Real-Time Trade Notifications</h3>
                      </div>
                      <p className="text-sm sm:text-base text-delphi-light/70 mb-3">
                        Experience complete transparency with live trade entry and exit notifications for your selected stocks. 
                        See exactly when we enter and exit positions - no delays, no edits, no hindsight adjustments.
                      </p>
                      <div className="p-3 bg-blue-500/5 border border-blue-500/20 rounded text-xs text-delphi-light/60">
                        <strong>Note:</strong> You'll receive trade execution prices only. Our proprietary methodology, 
                        position sizing algorithms, and decision criteria remain confidential for competitive reasons.
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-8 bg-delphi-red/20 rounded-full flex items-center justify-center">
                          <Shield size={16} className="text-delphi-red" />
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold">Verification Purpose Only</h3>
                      </div>
                      <p className="text-sm sm:text-base text-delphi-light/70 mb-3">
                        This program is designed purely for performance verification and transparency demonstration. 
                        The information provided is not investment advice and should not be used for independent trading decisions.
                      </p>
                      <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded text-xs text-delphi-light/60">
                        <strong>Important:</strong> Entry/exit notifications are for verification purposes only. 
                        They do not include timing, reasoning, or sufficient information for replication.
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-8 bg-delphi-red/20 rounded-full flex items-center justify-center">
                          <TrendingUp size={16} className="text-delphi-red" />
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold">Live Performance Tracking</h3>
                      </div>
                      <p className="text-sm sm:text-base text-delphi-light/70">
                        Track the performance of your hypothetical portfolio over 30 days using your selected starting amount. 
                        Watch your theoretical returns update in real-time based on our actual verified trades.
                      </p>
                    </div>
                    
                    <div className="p-4 sm:p-6 bg-delphi-red/5 border border-delphi-red/20 rounded-lg">
                      <div className="flex items-start">
                        <Info size={20} className="text-delphi-red mr-3 mt-1" />
                        <div>
                          <h4 className="text-base sm:text-lg font-medium mb-2">What You Will Receive</h4>
                          <ul className="text-xs sm:text-sm text-delphi-light/70 space-y-1">
                            <li>• Trade entry notifications with execution prices</li>
                            <li>• Trade exit notifications with execution prices</li>
                            <li>• Hypothetical portfolio performance updates</li>
                            <li>• Verified timestamps for all trade actions</li>
                          </ul>
                          <h4 className="text-base sm:text-lg font-medium mb-2 mt-4">What You Will NOT Receive</h4>
                          <ul className="text-xs sm:text-sm text-delphi-light/70 space-y-1">
                            <li>• Trading signals or investment advice</li>
                            <li>• Position sizing information</li>
                            <li>• Decision-making methodology</li>
                            <li>• Proprietary algorithms or models</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right Side - Signup Form */}
                <div className="data-section p-4 sm:p-6 md:p-8 bg-gradient-to-br from-delphi-gray/20 to-transparent backdrop-blur-sm">
                  <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Start Your 30-Day Transparency Window</h3>
                  
                  <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2">
                        Email Address
                      </label>
                      <input type="email" id="email" value={formData.email} onChange={handleEmailChange} className="w-full bg-delphi-gray/20 border border-delphi-gray/30 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-delphi-red/50" required placeholder="Enter your email" />
                    </div>
                    
                    {/* Starting Amount */}
                    <div>
                      <label className="block text-sm font-medium mb-3">
                        Hypothetical Starting Amount
                      </label>
                      <div className="space-y-2">
                        {startingAmountOptions.map(option => <label key={option.value} className="flex items-center cursor-pointer">
                            <input type="radio" name="startingAmount" value={option.value} checked={formData.startingAmount === option.value} onChange={e => handleStartingAmountChange(e.target.value)} className="mr-3 text-delphi-red focus:ring-delphi-red/50" required />
                            <span className="text-sm">{option.label}</span>
                          </label>)}
                      </div>
                      <p className="text-xs text-delphi-light/60 mt-2">
                        This amount will be used to calculate your hypothetical returns over the 30-day period.
                      </p>
                    </div>
                    
                    {/* Stock Selection */}
                    <div>
                      <label className="block text-sm font-medium mb-3">
                        Select Stocks to Track (Max 2)
                      </label>
                      
                      <div className="mb-3 p-3 bg-blue-500/5 border border-blue-500/20 rounded text-xs text-delphi-light/60">
                        <strong>Limited Selection:</strong> We've curated these high-volume, high-volatility targets that align with our fund's trading strategy to provide the most meaningful transparency experience.
                      </div>
                      
                      {stocksLoading ? <div className="text-center py-4">
                          <span className="text-delphi-light/70">Loading available stocks...</span>
                        </div> : <div className="space-y-2">
                          {domesticStocks.map(stock => {
                        const isSelected = formData.selectedStocks.some(s => s.startsWith(stock.symbol));
                        const canSelect = formData.selectedStocks.length < 2 || isSelected;
                        return <label key={stock.id} className={`flex items-center cursor-pointer p-2 rounded border transition-colors ${canSelect ? 'hover:bg-delphi-gray/10 border-delphi-gray/30' : 'opacity-50 cursor-not-allowed border-delphi-gray/20'} ${isSelected ? 'bg-delphi-red/10 border-delphi-red/30' : ''}`}>
                                <input type="checkbox" checked={isSelected} onChange={() => canSelect && handleStockSelection(stock.symbol, stock.name)} disabled={!canSelect} className="mr-3 text-delphi-red focus:ring-delphi-red/50" />
                                <div>
                                  <span className="font-medium">{stock.symbol}</span>
                                  <span className="text-sm text-delphi-light/70 ml-2">{stock.name}</span>
                                </div>
                              </label>;
                      })}
                        </div>}
                      
                      <p className="text-xs text-delphi-light/60 mt-2">
                        Selected: {formData.selectedStocks.length}/2 stocks
                      </p>
                    </div>
                    
                    {/* Disclaimer Box */}
                    <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                      <div className="flex items-start">
                        <AlertCircle size={16} className="text-amber-500 mr-2 mt-0.5" />
                        <div className="text-xs text-delphi-light/70">
                          <p className="font-medium mb-1">Transparency Verification Only</p>
                          <p>This service provides trade execution notifications solely for performance verification. 
                          The information cannot and should not be used for independent trading decisions. 
                          No proprietary methodology or actionable trading advice will be shared.</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Accredited Investor Confirmation */}
                    <div>
                      <label className="flex items-start cursor-pointer">
                        <input type="checkbox" checked={formData.accreditedConfirmed} onChange={handleAccreditedChange} className="mr-3 mt-1 text-delphi-red focus:ring-delphi-red/50" required />
                        <span className="text-xs text-delphi-light/70">
                          I confirm that I am an accredited investor as defined by the SEC, 
                          with either a net worth exceeding $1 million or an annual income exceeding $200,000 
                          ($300,000 for joint income) for the past two years with expectation to continue.
                        </span>
                      </label>
                    </div>
                    
                    {/* Submit Button */}
                    <div>
                      <button type="submit" disabled={isSubmitting || stocksLoading || formData.selectedStocks.length === 0} className="w-full px-4 py-3 bg-delphi-red text-white rounded-md font-medium flex items-center justify-center gap-2 hover:bg-red-600 transition-colors disabled:opacity-70">
                        {isSubmitting ? <>
                            <span className="animate-pulse">Starting Your Experience...</span>
                          </> : <>
                            <Send size={16} />
                            <span>Begin Transparency Window</span>
                          </>}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </PageLayout>;
};
export default Contact;